import { getPaymentCalendarDays } from './paymentCalendar';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const GHANA_TIME_ZONE = 'Africa/Accra';

export type WalletExpiryState = 'permanent' | 'unknown' | 'invalid' | 'expired' | 'urgent' | 'soon' | 'distant';

export interface WalletExpiryPresentation {
  label: string | null;
  exactLabel: string | null;
  state: WalletExpiryState;
  permitsActiveTerms: boolean;
  showClock: boolean;
}

const dateParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: GHANA_TIME_ZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const clockParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: GHANA_TIME_ZONE,
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h12',
});
const calendarParts = new Intl.DateTimeFormat('en-CA', {
  timeZone: GHANA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((item) => item.type === type)?.value || '';
}

function ghanaDate(date: Date): { day: string; month: string; year: string } {
  const parts = dateParts.formatToParts(date);
  return { day: part(parts, 'day'), month: part(parts, 'month'), year: part(parts, 'year') };
}

function ghanaTime(date: Date): string {
  const parts = clockParts.formatToParts(date);
  const hour = part(parts, 'hour');
  const minute = part(parts, 'minute');
  const dayPeriod = part(parts, 'dayPeriod').toLowerCase();
  return minute === '00' ? `${hour}${dayPeriod}` : `${hour}:${minute}${dayPeriod}`;
}

function exactExpiryLabel(expiresAt: Date): string {
  const date = ghanaDate(expiresAt);
  const parts = clockParts.formatToParts(expiresAt);
  return `${date.day} ${date.month} ${date.year}, ${part(parts, 'hour')}:${part(parts, 'minute')} ${part(parts, 'dayPeriod').toLowerCase()}`;
}

function futureExpiryLabel(expiresAt: Date, nowMs: number): Pick<WalletExpiryPresentation, 'label' | 'state' | 'showClock'> {
  const deltaMs = expiresAt.getTime() - nowMs;
  if (deltaMs < HOUR_MS) {
    if (deltaMs < MINUTE_MS) return { label: 'Expires in less than a minute', state: 'urgent', showClock: true };
    const minutes = Math.ceil(deltaMs / MINUTE_MS);
    return { label: `Expires in ${minutes} minute${minutes === 1 ? '' : 's'}`, state: 'urgent', showClock: true };
  }

  const calendarDays = getPaymentCalendarDays(expiresAt, nowMs);
  if (calendarDays === 0) return { label: `Expires today at ${ghanaTime(expiresAt)}`, state: 'urgent', showClock: true };
  if (calendarDays === 1) return { label: `Expires tomorrow at ${ghanaTime(expiresAt)}`, state: 'soon', showClock: true };
  if (calendarDays <= 7) return { label: `Expires in ${calendarDays} days`, state: 'soon', showClock: true };

  const expiryDate = ghanaDate(expiresAt);
  const currentYear = ghanaDate(new Date(nowMs)).year;
  const year = expiryDate.year === currentYear ? '' : ` ${expiryDate.year}`;
  return { label: `Expires ${expiryDate.day} ${expiryDate.month}${year}`, state: 'distant', showClock: false };
}

export function getWalletExpiryPresentation(
  value: string | null | undefined,
  nowMs = Date.now(),
): WalletExpiryPresentation {
  if (value === null) {
    return { label: 'Does not expire', exactLabel: null, state: 'permanent', permitsActiveTerms: true, showClock: false };
  }
  if (value === undefined || value === '') {
    return { label: null, exactLabel: null, state: 'unknown', permitsActiveTerms: true, showClock: false };
  }

  const expiresAt = new Date(value);
  if (Number.isNaN(expiresAt.getTime())) {
    return { label: 'Expiry unavailable', exactLabel: null, state: 'invalid', permitsActiveTerms: false, showClock: false };
  }
  if (expiresAt.getTime() <= nowMs) {
    return { label: 'Expired', exactLabel: exactExpiryLabel(expiresAt), state: 'expired', permitsActiveTerms: false, showClock: false };
  }

  return {
    ...futureExpiryLabel(expiresAt, nowMs),
    exactLabel: exactExpiryLabel(expiresAt),
    permitsActiveTerms: true,
  };
}

function nextGhanaMidnight(nowMs: number): number {
  const parts = calendarParts.formatToParts(new Date(nowMs));
  const year = Number(part(parts, 'year'));
  const month = Number(part(parts, 'month'));
  const day = Number(part(parts, 'day'));
  return Date.UTC(year, month - 1, day + 1);
}

export function getNextWalletExpiryRefreshDelay(values: Array<string | null | undefined>, nowMs = Date.now()): number | null {
  const delays = values.flatMap((value) => {
    if (!value) return [];
    const expiryMs = new Date(value).getTime();
    if (!Number.isFinite(expiryMs) || expiryMs <= nowMs) return [];
    const deltaMs = expiryMs - nowMs;
    if (deltaMs < HOUR_MS) return [Math.min(deltaMs, (deltaMs % MINUTE_MS) || MINUTE_MS)];
    return [Math.min(deltaMs - HOUR_MS, nextGhanaMidnight(nowMs) - nowMs)];
  });
  return delays.length ? Math.max(25, Math.min(...delays) + 25) : null;
}
